import type { UserStory } from "@swivl-mono/types";
import { Modal, ModalSize } from "@swivl-mono/ui";

import { UserStoryDetail } from "./UserStoryDetail";

interface UserStoryDetailModalProps {
  story: UserStory;
  /** Called when the user closes the modal (Esc, overlay click, X, or the
   *  detail's own close button). The host should clear the route param so the
   *  URL matches the closed-state. */
  onClose: () => void;
  onSaved: (story: UserStory) => void;
  onDeleted: (id: string) => void;
}

/**
 * Fullscreen modal wrapper for a single user-story detail view, rendered
 * **inline** inside `<AdminUserStoriesEditor>` (not via `ModalModel`).
 *
 * Rendering inline keeps Router + DomainProvider context flowing into the
 * modal content — the radix `<Dialog.Portal>` only relocates the DOM, not
 * the React tree, so `useNavigate` + `useDomain` inside `UserStoryDetail`
 * keep working.
 *
 * URL drives the open/close lifecycle: opening = `selectedStoryId` is set in
 * the URL, closing = navigate back to `/user-stories` (clears the param,
 * which unmounts the modal).
 */
export function UserStoryDetailModal({ story, onClose, onSaved, onDeleted }: UserStoryDetailModalProps): JSX.Element {
  const handleClose = (): void => onClose();

  return (
    <Modal
      isOpen={true}
      onClose={handleClose}
      title={
        <span className="flex items-center gap-3">
          <span className="font-mono text-sm text-grey-500">{story.id}</span>
          <span className="text-grey-300">·</span>
          <span>{story.title}</span>
        </span>
      }
      hasTitleBorder
      size={ModalSize.EXTRA_LARGE}
      className="flex h-[90vh] w-[min(1400px,95vw)]! max-w-[1400px]! flex-col"
    >
      <div className="min-h-0 flex-1 overflow-y-auto">
        <UserStoryDetail story={story} onSaved={onSaved} onDeleted={onDeleted} onClose={handleClose} />
      </div>
    </Modal>
  );
}
